import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { RouteTo } from '@routing/RouteMap';

const GET_CHECKOUT_STATE = gql`
  query GetCheckoutState {
    savedCheckoutState
  }
`;

/**
 * Komponente zur automatischen Weiterleitung zu unterbrochenen Checkout-Prozessen.
 * 
 * Diese Komponente prüft beim Login/App-Start, ob der Benutzer einen gespeicherten
 * Checkout-Zustand hat und leitet ihn automatisch zum Shop weiter, um den
 * unterbrochenen Checkout fortzusetzen.
 * 
 * Funktionalität:
 * - Prüft beim ersten Laden der App auf gespeicherte Checkout-Zustände
 * - Leitet automatisch zum Shop weiter wenn Checkout-Daten vorhanden sind
 * - Verhindert mehrfache Weiterleitungen durch lokale Zustandsverfolgung
 * - Respektiert explizite Navigation (überschreibt nicht bewusste Seitenaufrufe)
 * - Verhindert Weiterleitung von der Checkout-Erfolgsseite (um Endlosschleifen zu vermeiden)
 */
const CheckoutRedirectHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasCheckedForCheckout, setHasCheckedForCheckout] = useState(false);

  const [checkForSavedCheckout] = useLazyQuery(GET_CHECKOUT_STATE, {
    onCompleted: (data) => {
      // Prüfe ob gespeicherter Checkout-Zustand existiert
      if (data.savedCheckoutState && data.savedCheckoutState.selectedProducts) {
        const savedProducts = data.savedCheckoutState.selectedProducts;
        
        // Verhindere Weiterleitung von der Checkout-Erfolgsseite (um Endlosschleifen zu vermeiden)
        const isOnCheckoutReturnPage = location.pathname.includes('/payment/success/') || 
                                      location.pathname.includes('/payment/cancel/');
        
        // Nur weiterleiten wenn Produkte gespeichert sind, wir nicht bereits im Shop sind,
        // und wir nicht auf der Checkout-Erfolgsseite sind
        if (savedProducts.length > 0 && 
            location.pathname !== RouteTo.SHOP && 
            !isOnCheckoutReturnPage) {
          console.log('🔄 Unterbrochener Checkout erkannt - leite zum Shop weiter', {
            productCount: savedProducts.length,
            currentPath: location.pathname,
            redirectTo: RouteTo.SHOP,
            excludedFromRedirect: false
          });

          // Weiterleitung zum Shop mit Hinweis auf Wiederherstellung
          navigate(RouteTo.SHOP, { 
            replace: true,
            state: { 
              fromCheckoutRedirect: true,
              restoredProductCount: savedProducts.length 
            }
          });
        } else if (isOnCheckoutReturnPage) {
          console.log('🚫 Checkout-Weiterleitung übersprungen - auf Checkout-Erfolgsseite', {
            currentPath: location.pathname,
            productCount: savedProducts.length,
            reason: 'Verhindere Endlosschleife nach erfolgreichem Checkout'
          });
        }
      } else {
        console.log('📭 Kein gespeicherter Checkout-Zustand gefunden - normale Navigation');
      }
      
      setHasCheckedForCheckout(true);
    },
    onError: (error) => {
      console.warn('⚠️ Fehler beim Prüfen des Checkout-Zustands:', error.message);
      setHasCheckedForCheckout(true);
    }
  });

  useEffect(() => {
    // Nur einmal beim ersten Laden prüfen
    if (!hasCheckedForCheckout) {
      // Kleine Verzögerung um sicherzustellen dass Authentication vollständig geladen ist
      const timer = setTimeout(() => {
        console.log('🔍 Prüfe auf unterbrochenen Checkout-Prozess...');
        checkForSavedCheckout();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [hasCheckedForCheckout, checkForSavedCheckout]);

  // Diese Komponente rendert nichts - sie führt nur Seiteneffekte aus
  return null;
};

export default CheckoutRedirectHandler; 